import { BubbleRecommendations } from '@xp-utilities/web';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import BreakpointContext from '../../context/breakpoint';
import { PLACEMENT_IDS } from '../../tools/constants';
import parsePlacementIds from '../../tools/parsePlacementIds';
import styles from './BubbleRecommendationsWrapper.module.scss';
import useFeatureFlagQuery from './useFeatureFlagQuery';

/**
 * BubbleRecommendationsWrapper is a React component that conditionally renders
 * a section containing BubbleRecommendations based on feature flag and placement IDs.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.queryId - The identifier used to fetch placement IDs from
 *   PLACEMENT_IDS and feature flag.
 * @param {boolean} [props.skipQuery=false] - If true, skips the feature flag query
 *   and always renders recommendations.
 * @param {string} [props.testId='bubbles-recommendations-wrapper'] -
 *   The test ID for the wrapper section.
 * @returns {JSX.Element|null} The rendered BubbleRecommendations section
 *   or null if conditions are not met.
 */
export default function BubbleRecommendationsWrapper({
  queryId,
  skipQuery = false,
  testId = 'bubbles-recommendations-wrapper',
}) {
  const breakpoints = useContext(BreakpointContext);
  const placements = parsePlacementIds(`${PLACEMENT_IDS.mobile[queryId]}|${PLACEMENT_IDS.desktop[queryId]}`, breakpoints);
  const isFlagEnabled = useFeatureFlagQuery(queryId, skipQuery);

  return (
    placements.length > 0 && (isFlagEnabled || skipQuery) ? (
      <section className={styles.bubbleRecommendationsWrapper} data-testid={testId}>
        <BubbleRecommendations
          className={queryId ? styles[queryId] : ''}
          fetchRecommendations
          placementKey={placements[0]}
          placements={[...placements]}
        />
      </section>
    ) : null
  );
}

BubbleRecommendationsWrapper.propTypes = {
  queryId: PropTypes.string,
  skipQuery: PropTypes.bool,
  testId: PropTypes.string,
};
